// ProjectInspections — the project detail page's Inspections tab: a READ-ONLY,
// project-scoped inspections list (search toolbar, table, footer).
//
// Rows come from the same POST /inspections/all the inspections page uses,
// narrowed with the confirmed `project: [projectId]` body param, and they are
// rendered by the SAME shared column pack (useInspectionColumns) — called with
// no arguments here, so no row kebab renders. Nothing on this surface writes:
// there is no create, edit or delete affordance, and a row click hands off to
// /inspections rather than editing in place.
//
// Deliberate scope:
// - NO Filter dropdown and NO Columns menu, by user decision — the toolbar is
//   the search box and the row count, nothing else. Don't reinstate either as
//   an "improvement".
// - Since nothing narrows `state`, DRAFT and SUSPENDED inspections are listed
//   alongside active ones. `status` is required and STRICT (one value per
//   request) and this tab sends 'active', so archived ones stay out.
// - The `project` column is dropped from the shared pack — redundant when every
//   row belongs to this one project.
// - The row-click hand-off is gated on the inspections module's `view` verb:
//   in-layout routes exist only for ACCESSIBLE modules, so an ungated click
//   could navigate to a route that was never mounted.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Card, DataTable, Icon, IconButton, Input, TableFooter } from '@/components/common';
import { useGetInspectionsQuery } from '@/api/endpoints/inspectionApi';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useModuleAccess from '@/hooks/useModuleAccess';
import useStickyTotal from '@/hooks/useStickyTotal';
import { useInspectionColumns } from '@/pages/inspection-page/InspectionColumns';

interface ProjectInspectionsProps {
  projectId: string;
}

const ProjectInspections = ({ projectId }: ProjectInspectionsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  const trimmedSearch = debouncedSearch.trim();

  const { data, currentData, isLoading, isError } = useGetInspectionsQuery(
    {
      page,
      limit,
      status: 'active',
      project: [projectId],
      search: trimmedSearch || undefined,
    },
    { refetchOnMountOrArgChange: true },
  );

  // A new search term narrows the set — restart from page 1.
  useEffect(() => setPage(1), [debouncedSearch]);

  // No arguments = no actions column (see useInspectionColumns).
  const allColumns = useInspectionColumns();
  const columns = allColumns.filter((c) => c.key !== 'project');

  const items = data?.items ?? [];
  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, page);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  // canView gates the read hand-off AND the create one: in-layout routes exist
  // only for accessible modules, so without it either click would navigate to a
  // route that was never mounted.
  const { canView: canViewInspections, canCreate: canCreateInspections } =
    useModuleAccess('inspections');

  return (
    <div className='project-inspections'>
      <div className='project-inspections__toolbar'>
        <div className='project-inspections__toolbar-lead'>
          <div className='project-inspections__search'>
            <Input
              size='sm'
              iconStart='search'
              placeholder={t('inspection_search_ph')}
              aria-label={t('field_search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <IconButton
                className='project-inspections__search-clear'
                icon='x'
                variant='ghost'
                size='sm'
                aria-label={t('projects_search_clear')}
                onClick={() => setSearch('')}
              />
            )}
          </div>
        </div>
        <div className='project-inspections__toolbar-actions'>
          <span className='project-inspections__count'>
            {t('inspection_count', { count: total })}
          </span>
          {/* Creating happens on /inspections — this hands the project over in
              the router state, which opens the drawer there with the project
              PRESELECTED (still editable — preselect only, by decision). The
              button is hidden, not disabled, when the verb is missing. */}
          {canViewInspections && canCreateInspections && (
            <Button
              iconStart='plus'
              onClick={() => navigate('/inspections', { state: { createProjectId: projectId } })}
            >
              {t('inspection_new')}
            </Button>
          )}
        </div>
      </div>

      <Card padded={false} className='project-inspections__panel'>
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(i) => i.id}
          minWidth={1360}
          onRowClick={canViewInspections ? () => navigate('/inspections') : undefined}
          loading={isLoading}
          error={isError}
          loadingText={t('text_loading')}
          errorText={t('error_generic')}
          empty={
            <>
              <Icon name='clipboard-check' size={26} />
              {t('projects_inspections_empty')}
            </>
          }
        />

        <TableFooter
          page={page}
          limit={limit}
          total={total}
          onPage={setPage}
          onLimit={(n) => {
            setLimit(n);
            setPage(1);
          }}
          perPageLabel={t('inspection_items_per_page')}
          rangeText={t('inspection_range', { from, to, total })}
        />
      </Card>
    </div>
  );
};

export default ProjectInspections;
